import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

type TargetDescriptor = {
  entityRef: string;
  title: string;
  description?: string;
  scope: 'tenant' | 'global';
  provider: string;
  environment: string;
  region: string;
  moveEnabled: boolean;
  accountId?: string;
  vpcId?: string;
  subnetIds: string[];
  securityGroupIds: string[];
};

function isTargetsResponse(
  value: unknown,
): value is { targets: TargetDescriptor[] } {
  if (typeof value !== 'object' || value === null || !('targets' in value)) {
    return false;
  }
  const { targets } = value as { targets: unknown };
  return (
    Array.isArray(targets) &&
    targets.every(
      target =>
        typeof target === 'object' &&
        target !== null &&
        typeof (target as TargetDescriptor).entityRef === 'string' &&
        typeof (target as TargetDescriptor).title === 'string' &&
        ((target as TargetDescriptor).description === undefined ||
          typeof (target as TargetDescriptor).description === 'string') &&
        ['tenant', 'global'].includes((target as TargetDescriptor).scope) &&
        typeof (target as TargetDescriptor).provider === 'string' &&
        typeof (target as TargetDescriptor).environment === 'string' &&
        typeof (target as TargetDescriptor).region === 'string' &&
        typeof (target as TargetDescriptor).moveEnabled === 'boolean' &&
        ((target as TargetDescriptor).accountId === undefined ||
          typeof (target as TargetDescriptor).accountId === 'string') &&
        ((target as TargetDescriptor).vpcId === undefined ||
          typeof (target as TargetDescriptor).vpcId === 'string') &&
        Array.isArray((target as TargetDescriptor).subnetIds) &&
        (target as TargetDescriptor).subnetIds.every(
          subnetId => typeof subnetId === 'string',
        ) &&
        Array.isArray((target as TargetDescriptor).securityGroupIds) &&
        (target as TargetDescriptor).securityGroupIds.every(
          securityGroupId => typeof securityGroupId === 'string',
        ),
    )
  );
}

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      // Keep the status-only message when an intermediary returns non-JSON.
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

function Detail(props: { label: string; value: string }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="textSecondary">
        {props.label}
      </Typography>
      <Typography component="dd" variant="body2" style={{ margin: 0 }}>
        {props.value}
      </Typography>
    </>
  );
}

export function HorizonTargetPicker(
  props: FieldExtensionComponentProps<string>,
) {
  const { formData, onChange, rawErrors, required, disabled, readonly } = props;
  const fieldId = useId();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const requestSequence = useRef(0);
  const [targets, setTargets] = useState<TargetDescriptor[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchTargets = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('horizon-source');
      const response = await readJson<unknown>(
        await fetchApi.fetch(`${baseUrl}/v1/targets`),
      );
      if (!isTargetsResponse(response)) {
        throw new Error('The target service returned an invalid response');
      }
      if (sequence === requestSequence.current) {
        setTargets(response.targets);
        setLoaded(true);
      }
    } catch (caught) {
      if (sequence === requestSequence.current) {
        setError(
          caught instanceof Error ? caught.message : 'Unable to load targets',
        );
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  useEffect(() => {
    void fetchTargets();
    return () => {
      requestSequence.current += 1;
    };
  }, [fetchTargets]);

  const selectedTarget = targets.find(target => target.entityRef === formData);
  const unavailableSelection = loaded && Boolean(formData) && !selectedTarget;
  const locked = Boolean(disabled || readonly || loading);

  return (
    <Box>
      <TextField
        id={`${fieldId}-target`}
        select
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        required={required}
        disabled={locked || (loaded && targets.length === 0)}
        label="Approved target landing zone"
        value={formData ?? ''}
        onChange={event => onChange(event.target.value)}
        helperText="Only administrator-approved target profiles available to your tenant are listed."
        margin="normal"
        variant="outlined"
      >
        {targets.length > 0 && (
          <option value="">
            {required
              ? 'Select an approved target'
              : 'No destination — source-only discovery'}
          </option>
        )}
        {targets.map(target => (
          <option key={target.entityRef} value={target.entityRef}>
            {target.title}
          </option>
        ))}
        {unavailableSelection && (
          <option value={formData}>
            Previously selected destination — unavailable
          </option>
        )}
      </TextField>

      {unavailableSelection && (
        <Box role="alert">
          <Typography color="error">
            The selected destination is no longer available. Your unvalidated
            intent is retained; explicitly select an approved destination or
            clear it. Server-side validation is required before assessment or
            MOVE.
          </Typography>
          <Button disabled={locked} onClick={() => onChange('')}>
            Clear unavailable destination
          </Button>
        </Box>
      )}

      {loading && (
        <Box display="flex" alignItems="center" gridGap={8} mt={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Loading approved target profiles…
          </Typography>
        </Box>
      )}

      {error && (
        <Box mt={1}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button color="primary" size="small" onClick={fetchTargets}>
            Retry
          </Button>
        </Box>
      )}

      {loaded && !loading && !error && targets.length === 0 && (
        <Typography
          color={required ? 'error' : 'textSecondary'}
          variant="body2"
        >
          No approved target landing zones are available to your tenant.{' '}
          {required
            ? 'Contact the Horizon administrator.'
            : 'You can continue with source-only discovery. Destination-specific checks remain pending.'}
        </Typography>
      )}

      {selectedTarget && (
        <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={4}>
          <Typography variant="subtitle1">
            <strong>Selected target: {selectedTarget.title}</strong>
          </Typography>
          {selectedTarget.description && (
            <Typography variant="body2" color="textSecondary">
              {selectedTarget.description}
            </Typography>
          )}
          <Box
            component="dl"
            display="grid"
            gridTemplateColumns="minmax(8rem, max-content) minmax(0, 1fr)"
            gridColumnGap={16}
            gridRowGap={8}
            mt={2}
            mb={1}
          >
            <Detail label="Provider" value={selectedTarget.provider} />
            <Detail label="Environment" value={selectedTarget.environment} />
            <Detail label="Region" value={selectedTarget.region} />
            <Detail
              label="MOVE controls"
              value={
                selectedTarget.moveEnabled
                  ? 'Eligibility checks enabled; assessment still decides'
                  : 'ASSESS only — MGN configuration required before MOVE'
              }
            />
            <Detail
              label="Account"
              value={selectedTarget.accountId ?? 'Not specified'}
            />
            <Detail
              label="VPC"
              value={selectedTarget.vpcId ?? 'Not specified'}
            />
            <Detail
              label="Workload subnet(s)"
              value={
                selectedTarget.subnetIds.length
                  ? selectedTarget.subnetIds.join(', ')
                  : 'Not specified'
              }
            />
            <Detail
              label="Security group(s)"
              value={
                selectedTarget.securityGroupIds.length
                  ? selectedTarget.securityGroupIds.join(', ')
                  : 'Not specified'
              }
            />
            <Detail
              label="Scope"
              value={
                selectedTarget.scope === 'tenant'
                  ? 'Your tenant'
                  : 'Shared approved target'
              }
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            These read-only values come from the approved Catalog profile. This
            form submits only its Catalog reference.
          </Typography>
        </Box>
      )}

      {rawErrors?.length ? (
        <Typography color="error" variant="caption">
          {rawErrors.join('; ')}
        </Typography>
      ) : null}
    </Box>
  );
}
